/**
 * C6 — custom metrics and views join the catalogue as extra sources, so every
 * picker (widget picker, widget settings, KPIs) offers them with no special
 * case: "Custom metrics" (each metric one entry, by date) and one source per
 * view (its columns as metrics, its dimension column as the one breakdown).
 */

import type { Catalogue, CatalogueSource, MetricFormat } from '../reports/reports-api.ts';
import {
  CUSTOM_SOURCE,
  VIEW_PREFIX,
  type CustomMetric,
  type CustomView,
  type DataType,
} from './custom-api.ts';

export const FORMAT_OF: Record<DataType, MetricFormat> = {
  INTEGER: 'number',
  FLOAT: 'number',
  CURRENCY: 'money',
  PERCENT: 'percent',
};

/** A view widget's one breakdown: its dimension column. */
export const VIEW_DIMENSION = 'view';

function metricsSource(metrics: CustomMetric[]): CatalogueSource {
  return {
    key: CUSTOM_SOURCE,
    label: 'Custom metrics',
    category: 'Custom',
    needs_attachment: false,
    metrics: metrics.map((metric) => ({
      key: metric.id,
      label: metric.name,
      format: FORMAT_OF[metric.data_type],
      higher_is_better: metric.higher_is_better,
    })),
    dimensions: [{ key: 'date', label: 'Date' }],
  };
}

function viewSource(view: CustomView): CatalogueSource {
  return {
    key: `${VIEW_PREFIX}${view.id}`,
    label: `View: ${view.name}`,
    category: 'Views',
    needs_attachment: false,
    metrics: view.definition.columns.map((column) => ({
      key: column.id,
      label: column.name,
      format: FORMAT_OF[column.data_type],
      higher_is_better: column.higher_is_better,
    })),
    dimensions: [{ key: VIEW_DIMENSION, label: view.definition.dimension.name }],
  };
}

export function withCustom(
  catalogue: Catalogue,
  metrics: CustomMetric[],
  views: CustomView[] = [],
): Catalogue {
  const base = catalogue.sources.filter(
    (source) => source.key !== CUSTOM_SOURCE && !source.key.startsWith(VIEW_PREFIX),
  );
  return {
    ...catalogue,
    sources: [
      ...base,
      ...(metrics.length ? [metricsSource(metrics)] : []),
      ...views.map(viewSource),
    ],
  };
}
